import { useEffect, useRef, useState } from 'react';
import type { Vector } from '@/GraphEditor/helpers/Vector';
import type {
    CallIntegrationRequest,
    CallIntegrationResponse,
    FunctionDefinition,
    IntegrationAccess,
    Node,
    NodeInstance,
} from '@glueapp/graph-types';
import { NodeRenderer } from '../NodeRenderer';
import { makeExampleNodeInstance, makeFunctionExampleNodeInstance } from './makeExampleNodeInstance';
import type { LibraryElement } from './LibraryElement';

const PLACEHOLDER_COLOR = '#888888';

const placeholderNode: Node = {
    id: '__placeholder__',
    name: 'Block',
    description: '',
    category: '',
    color: PLACEHOLDER_COLOR,
    hasExecutionDecisions: false,
    findableInLibrary: false,
    isAsync: false,
    inPorts: [
        { name: 'Input', type: { kind: 'string' } },
        { name: 'Options', type: { kind: 'object', fields: {} }, optional: true },
    ],
    outPorts: [{ name: 'Result', type: { kind: 'string' } }],
    execute: () => ({ output: {} }),
} as unknown as Node;

const placeholderInstance: NodeInstance = {
    id: 0,
    nodeId: '__placeholder__',
    inPorts: placeholderNode.inPorts,
    outPorts: placeholderNode.outPorts,
    selected: false,
    portValues: {},
    portUserDefinedTypes: {},
    position: { x: 0, y: 0 },
    state: {},
};

interface Props {
    position: Vector;
    element?: LibraryElement;
    nodeDefinition?: Node;
    functions: FunctionDefinition[];
    callIntegration: (integrationAccess: IntegrationAccess, requestConfig: CallIntegrationRequest) => Promise<CallIntegrationResponse<any>>;
    onSelect?: () => void;
}

export const NodeLibraryPreview: React.FC<Props> = ({ position, element, nodeDefinition, functions, callIntegration, onSelect }) => {
    const isPlaceholder = !element || !nodeDefinition;

    const functionDef = element?.type === 'FUNCTION_INSTANCE' ? functions.find(func => func.id === element.functionId) : undefined;

    const node = nodeDefinition ?? placeholderNode;
    const nodeInstance = isPlaceholder
        ? placeholderInstance
        : functionDef
          ? makeFunctionExampleNodeInstance(node, functionDef)
          : makeExampleNodeInstance(node);

    const color = element?.color ?? PLACEHOLDER_COLOR;

    const previewPosition = position;

    // Fade in on element change
    const elementId = element?.id ?? '__placeholder__';
    const [opacity, setOpacity] = useState(0);
    const previousElementRef = useRef(elementId);

    useEffect(() => {
        if (elementId !== previousElementRef.current) {
            setOpacity(0);
            previousElementRef.current = elementId;
        }
        const frame = requestAnimationFrame(() => setOpacity(1));
        return () => cancelAnimationFrame(frame);
    }, [elementId]);

    // Only a real (non-placeholder) preview is clickable to add the node.
    const isClickable = !isPlaceholder && Boolean(onSelect);

    return (
        <div
            // Stop the pointer down from bubbling to the canvas's handleGraphMouseDown, which would
            // start a view drag and call setPointerCapture on the SVG. That capture retargets the
            // ensuing click to the SVG (firing handleGraphClick → hideNodeLibrary), so the preview's
            // own onClick would never fire. Adding the node on pointer down avoids that race entirely.
            onPointerDown={
                isClickable
                    ? event => {
                          event.stopPropagation();
                          onSelect?.();
                      }
                    : undefined
            }
            style={{
                position: 'absolute',
                transform: `translate(${previewPosition.x}px, ${previewPosition.y}px)`,
                pointerEvents: isClickable ? 'auto' : 'none',
                cursor: isClickable ? 'pointer' : 'default',
                filter: `drop-shadow(0 0 12px ${color}90) drop-shadow(0 0 30px ${color}60)`,
                opacity: isPlaceholder ? 0.5 : opacity,
                transition: 'opacity 150ms ease-out, filter 200ms ease-out',
            }}
        >
            <NodeRenderer
                nodeInstance={nodeInstance}
                node={node}
                key={nodeInstance.id}
                runTimes={new Set()}
                handleMouseDown={() => {}}
                handleMouseUp={() => {}}
                onClick={() => {}}
                onOutPortMouseDown={() => {}}
                onInPortMouseUp={() => {}}
                onInPortMouseDown={() => {}}
                setInPortPosition={() => {}}
                setOutPortPosition={() => {}}
                triggerManualRun={() => {}}
                callIntegration={callIntegration}
                setNodeDimensions={() => {}}
                setNodeState={() => {}}
                setNodeShape={() => {}}
                executionError={undefined}
                executionResult={undefined}
                nodeConnectionDictionary={{}}
                startNodeReframe={() => {}}
                endNodeReframe={() => {}}
            />
        </div>
    );
};
